import { useEffect, useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { recordRejection, type FinancierRecordFields } from './api';
import { todayKey } from './form';
import styles from './financier.module.css';

/**
 * „Ablehnung erfassen" (no frame; Finanzierer 08 shows refusals without PDFs): bank, day and reason of a bank's
 * refusal → `POST /financings/:id/offers/rejection`.
 */
export function RejectionDialog({
  open,
  financing,
  onClose,
  onDone,
}: {
  open: boolean;
  financing: FinancierRecordFields;
  onClose: () => void;
  onDone: () => void;
}) {
  const formId = useId();
  const [bank, setBank] = useState('');
  const [rejectedOn, setRejectedOn] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBank('');
    setRejectedOn(todayKey());
    setReason('');
    setErrors({});
    setBanner(null);
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    const next: Record<string, string> = {};
    if (bank.trim() === '') next['bank'] = 'Pflichtangabe.';
    if (!rejectedOn) next['rejectedOn'] = 'Pflichtangabe.';
    if (reason.trim() === '') next['reason'] = 'Pflichtangabe.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setPending(true);
    setBanner(null);
    try {
      await recordRejection(financing.id, {
        bank: bank.trim(),
        rejectedOn,
        reason: reason.trim(),
        requestedOn: financing.request?.requestedOn ?? null,
      });
      onDone();
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fields).length > 0) setErrors(error.fields);
      else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow={`${financing.numberLabel} · ${financing.linked.contact.name}`}
      title="Ablehnung erfassen"
      subtitle="Eine Bank hat ohne Angebot abgelehnt. Die Ablehnung steht danach in der Liste der Angebote."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Erfassen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <div className={styles.dialogGrid}>
          <TextField
            label="Bank"
            required
            value={bank}
            maxLength={200}
            onChange={(event) => setBank(event.target.value)}
            error={errors['bank']}
          />
          <TextField
            label="Abgelehnt am"
            type="date"
            required
            value={rejectedOn}
            max={todayKey()}
            onChange={(event) => setRejectedOn(event.target.value)}
            error={errors['rejectedOn']}
          />
        </div>
        <TextField
          label="Grund"
          required
          placeholder="z. B. Beleihungsauslauf zu hoch"
          value={reason}
          maxLength={500}
          onChange={(event) => setReason(event.target.value)}
          error={errors['reason']}
        />
      </form>
    </Dialog>
  );
}
